import { forwardRef, MouseEventHandler, ReactNode, Ref } from 'react';
import { Box, Stack } from '@o2/ui';
import { ChevronDown, ChevronUp, CustomerAvatarBusiness, CustomerAvatarResident } from '@o2/icons';
import { styled, SxProps, useMediaQuery, useTheme } from '@mui/material';
import colors from '@o2/ui/theme/colors';
import Tooltip, { TooltipProps } from './Tooltip';
import elmIds from './elmIds';

const StyledButton = styled('button', {
  name: 'AppBar',
  slot: 'UserButton',
})(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  background: 'none',
  border: 'none',
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  alignItems: 'center',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  padding: theme.spacing(2, 1, 2, 2.5),
  '& img': {
    height: 32,
  },
  '&:hover': {
    backgroundColor: colors.transparentWhite[100],
    textDecoration: 'underline',
    textUnderlineOffset: '0.25em',
  },
  '&:hover *': {
    textDecoration: 'underline',
    textUnderlineOffset: '0.25em',
  },
  '&:last-of-type': {
    marginRight: theme.spacing(-1),
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2, 2.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const Title = styled('div', {
  name: 'AppBarUserButton',
  slot: 'title',
})(() => ({
  fontSize: 12,
  fontWeight: 400,
  maxWidth: '180px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}));

const Subtitle = styled('div', {
  name: 'AppBarUserButton',
  slot: 'subtitle',
})(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.primary.main,
  maxWidth: '180px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}));

export interface UserButtonProps {
  /**
   * If you need your custom icon, you can override default icon by this prop.
   */
  icon?: ReactNode;

  /**
   * You can switch icon by user role
   */
  customerType?: 'resident' | 'business';

  /**
   * Main text in button
   */
  title: ReactNode;

  /**
   * Optional subtitle
   */
  subtitle?: ReactNode;
  children?: ReactNode;
  onClick?: MouseEventHandler;

  /**
   * If you need, you can show chevron icon on right side
   */
  showChevron?: boolean;

  /**
   * Defines whether the chevron is open or close
   */
  chevronOpen?: boolean;

  /**
   * Tooltip - show on hover (not working on touch screens)
   */
  tooltip?: ReactNode;

  /**
   * Reference
   */
  ref?: Ref<HTMLButtonElement>;

  /**
   * Props passed to the Tooltip component.
   */
  tooltipProps?: Omit<TooltipProps, 'title' | 'children' | 'ref'>;

  /**
   * Added custom styling for component
   */
  sx?: SxProps;
}

/**
 * Button displaying information about the logged-in user.
 */
const UserButton = forwardRef<HTMLButtonElement, UserButtonProps>(
  (
    {
      showChevron,
      chevronOpen,
      children,
      title,
      subtitle,
      icon,
      customerType,
      onClick,
      tooltip,
      tooltipProps,
      ...other
    },
    ref
  ) => {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const btn = (
      <StyledButton ref={ref} onClick={onClick} {...other}>
        {icon ? (
          icon
        ) : customerType === 'business' ? (
          <CustomerAvatarBusiness width={smallScreen ? 28 : 32} />
        ) : (
          <CustomerAvatarResident width={smallScreen ? 28 : 32} />
        )}
        {!smallScreen && (
          <>
            {children ? (
              children
            ) : (
              <Stack direction="column" gap={0.5}>
                <Title>{title}</Title>
                {subtitle && <Subtitle data-elm={elmIds.o2Id}>{subtitle}</Subtitle>}
              </Stack>
            )}
            <Box>
              {showChevron &&
                (chevronOpen ? <ChevronUp sx={{ fontSize: 16 }} /> : <ChevronDown sx={{ fontSize: 16 }} />)}
            </Box>
          </>
        )}
      </StyledButton>
    );

    return tooltip ? (
      <Tooltip title={tooltip} {...tooltipProps}>
        {btn}
      </Tooltip>
    ) : (
      btn
    );
  }
);

UserButton.displayName = 'AppBar.UserButton';

export default UserButton;
